import React, { useState, useEffect } from 'react';
import { Box, List, ListItemText, Checkbox, TableBody, TableCell, Table, CircularProgress } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
  MainContainer,
  DescriptionText,
  LayoutGrid,
  RolesPanel,
  RolesTitle,
  RoleItem,
  MappingPanel,
  MappingTitle,
  StyledTableContainer,
  StyledTableHead,
  StyledTableRow,
  SaveButton
} from './styles';
import { useBreadcrumb } from '../../../context/BreadcrumbContext';
import { useSidebar } from '../../../context/SidebarContext';
import {
  fetchRoleModuleMappings,
  createRoleModuleMapping,
  updateRoleModuleMapping,
} from '../../../modules/superadmin/actions';
import { fetchSystemRoles, fetchSystemModules, resetSuperAdminActionState } from '../../../modules/superadmin';
import {
  selectSystemRoles,
  selectSystemModules,
  selectRoleModuleMappings,
  selectCreateMappingLoading,
  selectUpdateMappingLoading,
  selectRoleModuleMappingsLoading
} from '../../../modules/superadmin/selectors';
import { AppDispatch } from '../../../store';
import AlertPopup from '../../../components/common/AlertPopup';

const RoleModuleMapping: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { setActivePage } = useSidebar();
  const { setPath, setIsShow } = useBreadcrumb();

  // Redux Selectors
  const roles = useSelector(selectSystemRoles);
  const modules = useSelector(selectSystemModules);
  const existingMappings = useSelector(selectRoleModuleMappings);
  const isCreating = useSelector(selectCreateMappingLoading);
  const isUpdating = useSelector(selectUpdateMappingLoading);



  const isSaving = isCreating || isUpdating;
  const isLoadingMapping = useSelector(selectRoleModuleMappingsLoading);

  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [localMappings, setLocalMappings] = useState<Record<number, any>>({});
  const [alert, setAlert] = useState<{ message: string, severity: 'success' | 'error' } | null>(null);

  useEffect(() => {
    setActivePage('role-module-mapping');
    setPath([
      { title: 'Super Admin', path: '/super-admin' },
      { title: 'Role Module Mapping', path: '/super-admin/role-module-mapping' },
    ]);
    setIsShow(true);

    dispatch(fetchSystemRoles());
    dispatch(fetchSystemModules());

    return () => {
      dispatch(resetSuperAdminActionState());
    }
  }, [setActivePage, setPath, setIsShow, dispatch]);

  useEffect(() => {
    if (roles.length > 0 && selectedRoleId === null) {
      setSelectedRoleId(roles[0].role_id);
    }
  }, [roles, selectedRoleId]);

  useEffect(() => {
    if (selectedRoleId) {
      dispatch(fetchRoleModuleMappings(selectedRoleId));
    }
  }, [selectedRoleId, dispatch]);

  useEffect(() => {
    if (existingMappings && Array.isArray(existingMappings)) {
      const mappingObj: Record<number, any> = {};
      existingMappings.forEach((m: any) => {
        // Handle both nested object and flat ID from API
        const moduleId = m.system_module?.module_id || m.system_module;
        if (moduleId) {
          mappingObj[moduleId] = m;
        }
      });
      setLocalMappings(mappingObj);
    }
  }, [existingMappings]);

  const handleRoleClick = (id: number) => {
    setSelectedRoleId(id);
  };

  const handlePermissionToggle = (moduleId: number, permission: string) => {
    setLocalMappings(prev => {
      const current = prev[moduleId] || {
        have_read: false,
        have_write: false,
        have_edit: false,
        have_delete: false,
        have_admin: false,
        system_module: moduleId,
        system_role: selectedRoleId
      };

      const newValue = !current[permission];
      let updatedPermissions = { ...current, [permission]: newValue };

      // If 'Admin' is toggled ON, enable all others
      if (permission === 'have_admin' && newValue) {
        updatedPermissions = {
          ...updatedPermissions,
          have_read: true,
          have_write: true,
          have_edit: true,
          have_delete: true,
        };
      }

      return {
        ...prev,
        [moduleId]: updatedPermissions
      };
    });
  };

  const handleSave = async () => {
    if (!selectedRoleId) return;

    // In a real scenario, we might want a bulk API.
    // Since we don't have one, we'll save each changed mapping.
    // For now, I'll just save all that are in localMappings.
    
    try {
      const promises = Object.values(localMappings).map((mapping: any) => {
        if (mapping.mapping_id) {
          return dispatch(updateRoleModuleMapping({
            mapping_id: mapping.mapping_id,
            have_read: mapping.have_read,
            have_write: mapping.have_write,
            have_edit: mapping.have_edit,
            have_delete: mapping.have_delete,
            have_admin: mapping.have_admin,
          }));
        } else {
          return dispatch(createRoleModuleMapping({
            system_role_id: selectedRoleId,
            system_module_id: mapping.system_module,
            have_read: mapping.have_read,
            have_write: mapping.have_write,
            have_edit: mapping.have_edit,
            have_delete: mapping.have_delete,
            have_admin: mapping.have_admin,
          }));
        }
      });

      await Promise.all(promises);
      setAlert({ message: 'Mappings updated successfully', severity: 'success' });
      dispatch(fetchRoleModuleMappings(selectedRoleId));
    } catch (error) {
      setAlert({ message: 'Failed to update mappings', severity: 'error' });
    }
  };

  return (
    <MainContainer>
      {alert && <AlertPopup message={alert.message} severity={alert.severity} open={!!alert} onClose={() => setAlert(null)} />}
      <DescriptionText>
        Configure module permissions for each system role.
      </DescriptionText>

      <LayoutGrid>
        <RolesPanel>
          <RolesTitle>System Roles</RolesTitle>
          <List component="nav" disablePadding>
            {Array.isArray(roles) && roles.map((role) => (
              <RoleItem
                key={role.role_id}
                selected={selectedRoleId === role.role_id}
                onClick={() => handleRoleClick(role.role_id)}
              >
                <ListItemText primary={role.role_name} />
              </RoleItem>
            ))}
          </List>
        </RolesPanel>

        <MappingPanel>
          <MappingTitle>
            Module Permissions
            {isLoadingMapping && <CircularProgress size={20} sx={{ ml: 2 }} />}
          </MappingTitle>

          <StyledTableContainer>
            <Table size="small">
              <StyledTableHead>
                <StyledTableRow>
                  <TableCell>Module Name</TableCell>
                  <TableCell>Module Type</TableCell>
                  <TableCell align="center">Read</TableCell>
                  <TableCell align="center">Write</TableCell>
                  <TableCell align="center">Edit</TableCell>
                  <TableCell align="center">Delete</TableCell>
                  <TableCell align="center">Admin</TableCell>
                </StyledTableRow>
              </StyledTableHead>
              <TableBody>
                {Array.isArray(modules) && modules.map((module) => {
                  const mapping = localMappings[module.module_id] || {};
                  return (
                    <StyledTableRow key={module.module_id}>
                      <TableCell>{module.module_name}</TableCell>
                      <TableCell>{module.module_type}</TableCell>
                      <TableCell align="center">
                        <Checkbox
                          size="small"
                          checked={!!mapping.have_read}
                          onChange={() => handlePermissionToggle(module.module_id, 'have_read')}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          size="small"
                          checked={!!mapping.have_write}
                          onChange={() => handlePermissionToggle(module.module_id, 'have_write')}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          size="small"
                          checked={!!mapping.have_edit}
                          onChange={() => handlePermissionToggle(module.module_id, 'have_edit')}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          size="small"
                          checked={!!mapping.have_delete}
                          onChange={() => handlePermissionToggle(module.module_id, 'have_delete')}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          size="small"
                          checked={!!mapping.have_admin}
                          onChange={() => handlePermissionToggle(module.module_id, 'have_admin')}
                        />
                      </TableCell>
                    </StyledTableRow>
                  );
                })}
              </TableBody>
            </Table>
          </StyledTableContainer>

          <Box sx={{ display: 'flow-root' }}>
            <SaveButton
              variant="contained"
              disableElevation
              onClick={handleSave}
              disabled={isSaving || !selectedRoleId}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </SaveButton>
          </Box>
        </MappingPanel>
      </LayoutGrid>
    </MainContainer>
  );
};

export default RoleModuleMapping;
